import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocaleFmt } from "@/lib/i18n/format";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw, RotateCcw, AlertTriangle, Pause, Play } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { adminModuleSyncAudit } from "@/lib/admin-modules.functions";
import { adminRetryWebhookEvent } from "@/lib/admin-webhooks.functions";

export const Route = createFileRoute("/_authenticated/app/admin/module-sync")({
  beforeLoad: async () => {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) throw redirect({ to: "/login" });
    const { data: roles } = await supabase.from("user_roles").select("role").eq("user_id", u.user.id);
    if (!roles?.some((r) => r.role === "admin")) throw redirect({ to: "/app" });
  },
  component: ModuleSyncAuditPage,
});

function ModuleSyncAuditPage() {
  const { t } = useTranslation();
  const { localeTag } = useLocaleFmt();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await adminModuleSyncAudit({ data: { limit: 100 } });
      setData(r);
      setLastUpdated(new Date());
    } catch (e: any) {
      if (!silent) toast.error(e?.message ?? t("pages.admin.loadError"));
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => load(true), 15000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  useEffect(() => {
    const channel = supabase
      .channel("admin-module-sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "webhook_events" }, () => load(true))
      .on("postgres_changes", { event: "*", schema: "public", table: "error_logs" }, () => load(true))
      .on("postgres_changes", { event: "*", schema: "public", table: "module_subscriptions" }, () => load(true))
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const retry = async (id: string) => {
    setRetrying(id);
    try {
      const r = await adminRetryWebhookEvent({ data: { id } });
      if (r.ok) toast.success(t("pages.admin.eventProcessed"));
      else toast.error(t("pages.admin.webhooksPage.retryFailedReason", { reason: r.error ?? t("pages.admin.genericError") }));
      await load();
    } finally { setRetrying(null); }
  };

  return (
    <>
      <PageHeader
        title={t("pages.admin.moduleSyncPage.title")}
        description={t("pages.admin.moduleSyncPage.description")}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch id="auto-refresh" checked={autoRefresh} onCheckedChange={setAutoRefresh} />
              <Label htmlFor="auto-refresh" className="text-xs flex items-center gap-1 cursor-pointer">
                {autoRefresh ? <Play className="h-3 w-3 text-primary" /> : <Pause className="h-3 w-3" />}
                {t("pages.admin.moduleSyncPage.autoRefresh")} {autoRefresh ? t("pages.admin.moduleSyncPage.on") : t("pages.admin.moduleSyncPage.off")}
              </Label>
            </div>
            {lastUpdated && (
              <span className="text-xs text-muted-foreground">{t("pages.admin.moduleSyncPage.updatedAt", { time: lastUpdated.toLocaleTimeString(localeTag) })}</span>
            )}
            <Button asChild variant="outline" size="sm"><Link to="/app/admin/modules">{t("pages.admin.moduleSyncPage.backModules")}</Link></Button>
            <Button variant="outline" size="sm" onClick={() => load(false)}><RefreshCw className="mr-2 h-4 w-4" />{t("pages.admin.reload")}</Button>
          </div>
        }
      />
      <PageBody>
        {loading || !data ? <p className="text-sm text-muted-foreground">{t("pages.admin.loading")}</p> : (
          <>
            <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { label: t("pages.admin.moduleSyncPage.webhookEvents"), value: data.stats.totalEvents },
                { label: t("pages.admin.moduleSyncPage.failedEvents"), value: data.stats.failedEvents, warn: data.stats.failedEvents > 0 },
                { label: t("pages.admin.moduleSyncPage.activeSubs"), value: data.stats.activeSubs },
                { label: t("pages.admin.moduleSyncPage.canceledSubs"), value: data.stats.canceledSubs },
              ].map((s) => (
                <Card key={s.label}>
                  <CardContent className="flex items-center gap-3 pt-6">
                    <AlertTriangle className={`h-5 w-5 ${s.warn ? "text-destructive" : "text-muted-foreground"}`} />
                    <div>
                      <div className="text-2xl font-bold">{s.value}</div>
                      <div className="text-xs text-muted-foreground">{s.label}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="mb-6">
              <CardHeader><CardTitle>{t("pages.admin.moduleSyncPage.recentWebhooks")}</CardTitle></CardHeader>
              <CardContent>
                <div className="max-h-[40vh] overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("pages.admin.cols.when")}</TableHead>
                        <TableHead>{t("pages.admin.cols.type")}</TableHead>
                        <TableHead>{t("pages.admin.cols.env")}</TableHead>
                        <TableHead>{t("pages.admin.cols.status")}</TableHead>
                        <TableHead>{t("pages.admin.cols.error")}</TableHead>
                        <TableHead className="text-right">{t("pages.admin.actions")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.events.map((e: any) => (
                        <TableRow key={e.id}>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(e.created_at).toLocaleString(localeTag)}</TableCell>
                          <TableCell className="font-mono text-xs">{e.event_type}</TableCell>
                          <TableCell><Badge variant="outline">{e.environment}</Badge></TableCell>
                          <TableCell><Badge variant={e.status === "failed" ? "destructive" : e.status === "processed" ? "secondary" : "default"}>{e.status}</Badge></TableCell>
                          <TableCell className="max-w-[300px] text-xs text-muted-foreground line-clamp-2">{e.error ?? "-"}</TableCell>
                          <TableCell className="text-right">
                            {e.status !== "processed" && (
                              <Button size="sm" disabled={retrying === e.id} onClick={() => retry(e.id)}>
                                <RotateCcw className="mr-1 h-3 w-3" />{retrying === e.id ? "…" : t("pages.admin.retry")}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {data.events.length === 0 && <TableRow><TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">{t("pages.admin.noEvents")}</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardHeader><CardTitle>{t("pages.admin.moduleSyncPage.syncErrors")}</CardTitle></CardHeader>
              <CardContent>
                <div className="max-h-[30vh] overflow-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>{t("pages.admin.cols.when")}</TableHead><TableHead>{t("pages.admin.cols.source")}</TableHead><TableHead>{t("pages.admin.cols.level")}</TableHead><TableHead>{t("pages.admin.cols.message")}</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {data.errors.map((e: any) => (
                        <TableRow key={e.id}>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(e.created_at).toLocaleString(localeTag)}</TableCell>
                          <TableCell className="font-mono text-xs">{e.source}</TableCell>
                          <TableCell><Badge variant={e.level === "error" ? "destructive" : "outline"}>{e.level}</Badge></TableCell>
                          <TableCell className="text-xs">{e.message}</TableCell>
                        </TableRow>
                      ))}
                      {data.errors.length === 0 && <TableRow><TableCell colSpan={4} className="py-6 text-center text-sm text-muted-foreground">{t("pages.admin.moduleSyncPage.noErrors")}</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>{t("pages.admin.moduleSyncPage.recentSubs")}</CardTitle></CardHeader>
              <CardContent>
                <div className="max-h-[30vh] overflow-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>{t("pages.admin.moduleSyncPage.user")}</TableHead><TableHead>{t("pages.admin.moduleSyncPage.module")}</TableHead><TableHead>{t("pages.admin.cols.status")}</TableHead><TableHead>{t("pages.admin.cols.env")}</TableHead><TableHead>{t("pages.admin.moduleSyncPage.expiry")}</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {data.subs.map((s: any, i: number) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono text-xs">{s.user_id?.slice(0, 8)}…</TableCell>
                          <TableCell className="text-xs">{s.module_id}</TableCell>
                          <TableCell><Badge variant={s.status === "active" ? "default" : "secondary"}>{s.status}</Badge></TableCell>
                          <TableCell><Badge variant="outline">{s.environment}</Badge></TableCell>
                          <TableCell className="text-xs text-muted-foreground">{s.current_period_end ? new Date(s.current_period_end).toLocaleDateString(localeTag) : "-"}</TableCell>
                        </TableRow>
                      ))}
                      {data.subs.length === 0 && <TableRow><TableCell colSpan={5} className="py-6 text-center text-sm text-muted-foreground">{t("pages.admin.moduleSyncPage.noSubs")}</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </PageBody>
    </>
  );
}
